import { equals, has, isEmpty, keys, trim } from 'ramda'
import { v1 as uuid } from 'uuid'
import { z } from 'zod'

import { cannot } from '@/app/helpers'
import { getMessageLabel } from '@/app/module/campaigns/definitions'
import {
  ConditionActions,
  ContentAudio,
  ContentSMS,
  ContentWhatsApp,
  LocalOpenEnded,
  LocalWhatsAppButtons,
  LocalWhatsAppList,
  Part,
  PartTypes,
  Translation,
} from '@/app/module/campaigns/types'
import { Action } from '@/app/module/campaigns/types/schemas/actions/all-actions'
import { getActions } from '@/app/module/campaigns/utils/part-actions'
import { getPartContentPreview } from '@/app/module/campaigns/utils/part-content'

export { getActions, nestedActions } from '@/app/module/campaigns/utils/part-actions'

export const defaultOnAnswerCATI = [
  {
    reply: 'yes',
    ranges: [],
    actions: [],
    label: '',
  },
  {
    reply: 'no',
    ranges: [],
    actions: [],
    label: '',
  },
]

export const defaultOnAnswerCATINoActions = defaultOnAnswerCATI.map((item) => ({
  ...item,
  actions: [],
}))

export const defaultOnAnswerText = [
  {
    replies: ['yes'],
    reply: 'yes',
    ranges: [],
    actions: [],
    label: '',
  },
  {
    replies: ['no'],
    reply: 'no',
    ranges: [],
    actions: [],
    label: '',
  },
]

export const defaultOnAnswerTextNoActions = defaultOnAnswerText.map((item) => ({
  ...item,
  actions: [],
}))

export const defaultOnAnswerVoice = [
  {
    replies: ['1'],
    ranges: [],
    actions: [],
    label: '',
  },
  {
    replies: ['2'],
    ranges: [],
    actions: [],
    label: '',
  },
]

export const defaultOnAnswerVoiceNoActions = defaultOnAnswerVoice.map((item) => ({
  ...item,
  actions: [],
}))

export const defaultSpokenResponse = {
  actions: [],
  silence: 3,
  maxSeconds: 10,
  stopKey: '#',
  transcribe: false,
}

export const generateDefaultOnButtons = (noAction?: boolean): LocalWhatsAppButtons => ({
  buttons: [
    {
      id: uuid(),
      actions: noAction ? [] : [],
      label: '',
      title: 'yes',
    },
    {
      id: uuid(),
      actions: noAction ? [] : [],
      label: '',
      title: 'no',
    },
  ],
})

export const generateDefaultOnList = (noAction?: boolean): LocalWhatsAppList => ({
  button: '',
  items: [
    {
      id: uuid(),
      actions: noAction ? [] : [],
      description: '',
      label: '',
      title: 'yes',
    },
    {
      id: uuid(),
      actions: noAction ? [] : [],
      description: '',
      label: '',
      title: 'no',
    },
  ],
})

export const trimContent = (content?: string, fallback = '') => (content ? trim(content) : fallback)

export const concatAll = <T>(arr: Array<T[] | undefined>): T[] => arr.flatMap((a) => a || [])

export const findJumpsSource = (id: string, parts: Part[]): string[] =>
  parts
    .reduce((partAccum: PartWithIndex[], part, idx): PartWithIndex[] => {
      if (part.id === id) {
        // skip the current part because it cannot jump to itself
        // and because it might be a broken part
        return partAccum
      }

      let accum = partAccum
      if (getActions(part).find((a) => has('jump', a) && a.jump.nextPart === id)) {
        accum = [...partAccum, { part, idx }]
      }
      return accum
    }, [] as PartWithIndex[])
    .map(getJumpLabel)

type PartWithIndex = {
  part: Part
  idx: number
}

const getJumpLabel = (p: PartWithIndex): string =>
  `${p.idx + 1}. ${getPartLabel(p.part)} ${getPartContentPreview(p.part)}`

export const getPartType = (part: Part): PartTypes => {
  if (has(PartTypes.ActionsOnly, part)) {
    return PartTypes.ActionsOnly
  }
  if (has(PartTypes.QuotaActions, part)) {
    return PartTypes.QuotaActions
  }
  if (has(PartTypes.ApiCall, part)) {
    return PartTypes.ApiCall
  }
  if (has(PartTypes.CATIMessage, part)) {
    return PartTypes.CATIMessage
  }
  if (has(PartTypes.CATIQuestion, part)) {
    return PartTypes.CATIQuestion
  }
  if (has(PartTypes.SMSMessage, part)) {
    return PartTypes.SMSMessage
  }
  if (has(PartTypes.SMSQuestion, part)) {
    return PartTypes.SMSQuestion
  }
  if (has(PartTypes.Topup, part)) {
    return PartTypes.Topup
  }
  if (has(PartTypes.VoiceMessage, part)) {
    return PartTypes.VoiceMessage
  }
  if (has(PartTypes.VoiceQuestion, part)) {
    return PartTypes.VoiceQuestion
  }
  if (has(PartTypes.WhatsAppMessage, part)) {
    return PartTypes.WhatsAppMessage
  }
  if (has(PartTypes.WhatsAppQuestion, part)) {
    return PartTypes.WhatsAppQuestion
  }
  if (has(PartTypes.WhatsAppFlow, part)) {
    return PartTypes.WhatsAppFlow
  }
  return cannot(part)
}

const getPartLabel = (part: Part): string => getMessageLabel(getPartType(part))

export const getActionsFromConditionActions = (actions: ConditionActions[]): Action[] => {
  return actions.flatMap((a) => a.actions || [])
}

export const hasAudioContent = (content: unknown) =>
  has('playfile', content) || has('say', content) || has('voice', content)

export const hasTextContent = (content: unknown) => has('text', content)

const translationSchema = z.object({
  document: z.string().optional(),
  image: z.string().optional(),
  playfile: z.string().optional(),
  text: z.string().optional(),
  say: z.string().optional(),
  voice: z.string().optional(),
})

export const isTranslation = (t: unknown): t is Translation => {
  if (!t || isEmpty(t)) {
    return false
  }

  try {
    translationSchema.strict().parse(t)
    return true
  } catch {
    return false
  }
}

export const isTranslations = (t: unknown): t is Record<string, Translation> => {
  if (!t || isEmpty(t)) {
    return false
  }

  try {
    // check if t is an object
    z.object({}).parse(t)
    // if t is an object, check if it has translations
    return !keys(t).some((k) => !isTranslation(t[k]))
  } catch {
    return false
  }
}

export const hasTranslations = (content: unknown): content is { translations: Record<string, Translation> } => {
  if (!has('translations', content)) {
    return false
  }

  return isTranslations(content.translations) && Object.values(content.translations).some((t) => isTranslation(t))
}

export const isContentAudio = (content: unknown): content is ContentAudio => {
  const hasContent = hasAudioContent(content)
  if (!hasTranslations(content)) {
    return hasContent
  }

  return hasContent || Object.values(content.translations).some(hasAudioContent)
}

export const isContentText = (content: unknown): content is ContentSMS => {
  const hasContent = hasTextContent(content)
  if (!hasTranslations(content)) {
    return hasContent
  }

  return hasContent || Object.values(content.translations).some(hasTextContent)
}

export const isOpenEndedModified = (openEnded?: LocalOpenEnded) => {
  if (!openEnded) {
    return false
  }

  const defaultOEActions = {
    actions: [],
  }
  const defaultCategories = [
    {
      category: '',
      actions: [],
    },
    {
      category: '',
      actions: [],
    },
  ]

  const currentActions = {
    actions: openEnded.actions,
  }
  const currentCategories = openEnded.categories

  const isActionModified = !equals(currentActions, defaultOEActions)
  const isCategoryModified = !!currentCategories && !equals(currentCategories, defaultCategories)
  const isValidationModified = !!openEnded.regexpEnabled || !!openEnded.format

  return isActionModified || isCategoryModified || isValidationModified
}

/**
 * Get a map of languages and whether the sms content is available in that language
 */
export const getSMSLanguageMap = (content: ContentSMS, defaultLanguage: string): Record<string, boolean> => {
  if (!defaultLanguage) {
    return {}
  }
  return {
    [defaultLanguage]: !!content.text,
    ...Object.fromEntries(Object.entries(content.translations || {}).map(([lang, trans]) => [lang, !!trans.text])),
  }
}

/**
 * Get a map of languages and whether the audio content is available in that language
 */
export const getAudioLanguageMap = (content: ContentAudio, defaultLanguage: string): Record<string, boolean> => {
  if (!defaultLanguage) {
    return {}
  }
  return {
    [defaultLanguage]: !!content.playfile || (!!content.say && !!content.voice),
    ...Object.fromEntries(
      Object.entries(content.translations || {}).map(([lang, trans]) => [
        lang,
        !!trans.playfile || (!!trans.say && !!trans.voice),
      ]),
    ),
  }
}

/**
 * Get a map of languages and whether the whatsapp content is available in that language
 */
export const getWALanguageMap = (content: ContentWhatsApp, defaultLanguage: string): Record<string, boolean> => ({
  [defaultLanguage]: !!content.audio || !!content.document || !!content.image || !!content.text || !!content.video,
  ...Object.fromEntries(
    Object.entries(content.translations || {}).map(([lang, trans]) => [
      lang,
      !!trans.audio || !!trans.document || !!trans.image || !!trans.text || !!trans.video,
    ]),
  ),
})
